
import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import { ZoomIn, ZoomOut, Maximize, MousePointerClick, Move, Share2 } from 'lucide-react';
import {
    useGraphData,
    useNodeSelection,
    NODE_TYPE_MAP,
    NODE_STYLE_MAP,
    LINK_COLORS,
    GraphNodeKind,
    D3Node,
    D3Link
} from '../hooks/useGraphVisualization';

const LinkExplorerPage: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const gRef = useRef<SVGGElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // Use shared hooks for data and selection
    const { allNodes, allLinks } = useGraphData();
    const { selectedNodes, toggleNodeSelection, toggleColumnSelection, selectAll } = useNodeSelection(allNodes, allLinks);

    // D3 State
    const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);

    // --- SETUP & RESIZE ---
    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                setDimensions({ width: entry.contentRect.width, height: entry.contentRect.height });
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    // --- INITIALIZATION (Zoom & Static Layout) ---
    useEffect(() => {
        if (allNodes.length === 0 || dimensions.width === 0 || !svgRef.current) return;

        const svg = d3.select(svgRef.current);

        if (!zoomRef.current) {
            zoomRef.current = d3.zoom<SVGSVGElement, unknown>()
                .scaleExtent([0.1, 4])
                .on('zoom', (event) => {
                    d3.select(gRef.current).attr('transform', event.transform);
                });
            svg.call(zoomRef.current).on("dblclick.zoom", null);
        }

        const defs = svg.select('defs').size() ? svg.select('defs') : svg.append('defs');
        defs.selectAll('*').remove();
        LINK_COLORS.forEach(color => {
            const safeId = color.startsWith('var') ? (color.includes('prev') ? 'prev' : 'det') : color.replace('#', '');
            defs.append("marker")
                .attr("id", `arrow-explorer-${safeId}`)
                .attr("viewBox", "0 -5 10 10")
                .attr("refX", 14)
                .attr("refY", 0)
                .attr("markerWidth", 6)
                .attr("markerHeight", 6)
                .attr("orient", "auto")
                .append("path")
                .attr("d", "M0,-5L10,0L0,5")
                .attr("fill", color);
        });

    }, [dimensions.width, allNodes.length]);

    // --- RENDER & UPDATE LOOP ---
    useEffect(() => {
        if (!gRef.current || allNodes.length === 0 || dimensions.width === 0) return;

        const g = d3.select(gRef.current);

        const colWidth = 320;
        const rowHeight = 70;
        const columnsOrder: GraphNodeKind[] = ['p', 'f', 'c-prev', 'c-det', 'd'];
        const columns = new Map(columnsOrder.map(k => [k, [] as D3Node[]]));
        allNodes.forEach(n => columns.get(n.kind)?.push(n));

        const totalGridWidth = columnsOrder.length * colWidth;
        const startX = Math.max(50, dimensions.width / 2 - totalGridWidth / 2 + colWidth / 2);

        columnsOrder.forEach((kind, colIndex) => {
            const list = columns.get(kind)!;
            const xPos = startX + colIndex * colWidth;
            const startY = 100;

            list.forEach((n, i) => {
                n.x = xPos;
                n.y = startY + (i * rowHeight);
            });
        });

        const links = g.select(".links").selectAll<SVGPathElement, D3Link>("path")
            .data(allLinks, (d: any) => `${(d.source as D3Node).id || d.source}-${(d.target as D3Node).id || d.target}`);

        links.join("path")
            .attr("d", d => {
                const s = allNodes.find(n => n.id === ((d.source as any).id || d.source));
                const t = allNodes.find(n => n.id === ((d.target as any).id || d.target));
                if (!s || !t) return "";
                return `M${s.x},${s.y}L${t.x},${t.y}`;
            })
            .attr("fill", "none")
            .attr("stroke", d => d.color)
            .attr("stroke-dasharray", d => d.dash || null)
            .attr("marker-end", d => `url(#arrow-explorer-${d.color.replace(/[^a-zA-Z0-9-]/g, '')})`)
            .transition().duration(300)
            .style("opacity", d => {
                const sId = (d.source as any).id || d.source;
                const tId = (d.target as any).id || d.target;
                return selectedNodes.has(sId) && selectedNodes.has(tId) ? 1 : 0.15;
            })
            .attr("stroke-width", d => {
                const sId = (d.source as any).id || d.source;
                const tId = (d.target as any).id || d.target;
                return selectedNodes.has(sId) && selectedNodes.has(tId) ? 2.5 : 1;
            });

        const nodes = g.select(".nodes").selectAll<SVGGElement, D3Node>("g.node-group")
            .data(allNodes, d => d.id);

        const nodesEnter = nodes.enter().append("g")
            .attr("class", "node-group")
            .style("cursor", "pointer");

        nodesEnter.merge(nodes as any)
            .attr("transform", d => `translate(${d.x},${d.y})`)
            .on("click", (e, d) => {
                toggleNodeSelection(d.id, e.shiftKey || true);
            })
            .each(function (d) {
                const el = d3.select(this);
                const isSelected = selectedNodes.has(d.id);
                const style = NODE_STYLE_MAP[d.kind];

                el.selectAll('*').remove();

                el.append('rect')
                    .attr('width', d.w).attr('height', d.h).attr('x', -d.w / 2).attr('y', -d.h / 2)
                    .attr('rx', 12).attr('ry', 12)
                    .attr('fill', style.fill)
                    .attr('stroke', style.stroke)
                    .attr('stroke-width', isSelected ? 3 : 1.5)
                    .style('opacity', isSelected ? 1 : 0.5)
                    .style('filter', isSelected ? 'drop-shadow(0 4px 6px rgba(0,0,0,0.15))' : 'none');

                el.append('text')
                    .text(d.text.length > 35 ? d.text.substring(0, 32) + '...' : d.text)
                    .attr('dy', 5).attr('dx', 12)
                    .attr('text-anchor', 'middle')
                    .attr('font-size', '12px')
                    .style('font-family', 'sans-serif')
                    .style('fill', style.text)
                    .style('font-weight', isSelected ? 'bold' : 'normal')
                    .style('pointer-events', 'none');

                const checkX = -d.w / 2 + 16;
                el.append('circle')
                    .attr('cx', checkX).attr('cy', 0)
                    .attr('r', 9)
                    .attr('fill', isSelected ? style.stroke : '#fff')
                    .attr('stroke', style.stroke)
                    .attr('stroke-width', 1.5);

                if (isSelected) {
                    el.append('path')
                        .attr('d', `M${checkX - 3.5},-1 L${checkX - 0.5},3 L${checkX + 4.5},-4`)
                        .attr('stroke', '#fff')
                        .attr('stroke-width', 2)
                        .attr('fill', 'none');
                }
            });

        nodes.exit().remove();

    }, [allNodes, allLinks, dimensions, selectedNodes, toggleNodeSelection]);


    const handleZoom = (factor: number) => {
        if (!zoomRef.current || !svgRef.current) return;
        d3.select(svgRef.current).transition().duration(300).call(zoomRef.current.scaleBy, factor);
    };
    const handleResetZoom = () => {
        if (!zoomRef.current || !svgRef.current) return;
        d3.select(svgRef.current).transition().duration(750).call(zoomRef.current.transform, d3.zoomIdentity);
    };


    return (
        <div className="h-full flex flex-col gap-4 p-4">
            <div className="flex justify-between items-center bg-card p-4 rounded-lg border border-border shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                        <Share2 className="text-primary" /> Link Explorer
                    </h1>
                    <p className="text-text-secondary text-sm">Click nodes to select data branches and explore connections.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex bg-background border border-border rounded-lg p-1">
                        {(Object.keys(NODE_TYPE_MAP) as GraphNodeKind[]).map(kind => (
                            <button key={kind} onClick={() => toggleColumnSelection(kind)} className="px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors">
                                {NODE_TYPE_MAP[kind]}
                            </button>
                        ))}
                    </div>
                    <button onClick={selectAll} className="px-3 py-1.5 text-xs font-medium bg-background border border-border rounded-lg hover:bg-slate-100 transition-colors">Select All</button>
                </div>
            </div>

            <div className="flex-1 bg-card border border-border rounded-lg shadow-sm relative overflow-hidden" ref={containerRef}>
                <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                    <button onClick={() => handleZoom(1.2)} className="p-2 bg-white dark:bg-slate-800 border border-border rounded-md shadow-sm hover:bg-slate-50"><ZoomIn size={18} /></button>
                    <button onClick={() => handleZoom(0.8)} className="p-2 bg-white dark:bg-slate-800 border border-border rounded-md shadow-sm hover:bg-slate-50"><ZoomOut size={18} /></button>
                    <button onClick={() => handleResetZoom} className="p-2 bg-white dark:bg-slate-800 border border-border rounded-md shadow-sm hover:bg-slate-50"><Maximize size={18} /></button>
                </div>
                <div className="absolute top-4 left-4 z-10 pointer-events-none bg-card/90 backdrop-blur p-2 rounded border border-border text-xs text-text-secondary shadow-sm">
                    <div className="flex items-center gap-2"><MousePointerClick size={14} /> Click to select branch</div>
                    <div className="flex items-center gap-2 mt-1"><Move size={14} /> Drag to Pan, Scroll to Zoom</div>
                </div>

                <svg ref={svgRef} className="w-full h-full cursor-move bg-slate-50 dark:bg-slate-900/50">
                    <g ref={gRef}>
                        <g className="links"></g>
                        <g className="nodes"></g>
                    </g>
                </svg>
            </div>
        </div>
    );
};

export default LinkExplorerPage;
